import awsExports from '../aws-exports';
import { GraphQLAPI, graphqlOperation } from '@aws-amplify/api-graphql';
import { generateClient } from '@aws-amplify/api';
import { get as awsGet, post } from 'aws-amplify/api';
import {
  fetchAuthSession,
  getCurrentUser,
  signUp,
  updatePassword,
} from 'aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import {
  setDispatcherListing,
  setWaiterListing,
  setFilters,
  setListing,
  setSelected,
  setSelectedFilters,
  setPaginationList,
  setAdminType,
  setNextToken,
} from '../store/ducks/admins';
import { HeadCell } from '../models/dataTable';
import { AdminsApiPaths, AdminsConstants } from '../constants/enums';
import useApp from './useApp';
import * as AWS from 'aws-sdk';
import awsmobile from '../aws-exports';

const client = generateClient();
const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, showConfirm } = useApp();
  const listing = useSelector(
    (state: any) => state[`${listingName}`]?.listing || [],
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]['searchText'],
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]['selected'],
  );
  const account = useSelector((state: any) => state.accounts.selected);
  const paginationList = useSelector(
    (state: any) => state.admins.paginationList,
  );
  const adminType = useSelector((state: any) => state.admins.adminType);

  const COGNITO_GROUPS = [
    { label: 'admin', value: 'admin' },
    // { label: 'staff', value: 'staff' },
  ];
  const DEVELOP_COGNITO_USER_POOL_ID = awsmobile.aws_user_pools_id;

  const DEVELOP_COGNITO_IDENTITY_POOL_ID =
    awsmobile.aws_cognito_identity_pool_id;

  // const DEVELOP_COGNITO_USER_POOL_ID = "us-east-2_l6TmJKRsb";
  // const PRODUCTION_COGNITO_USER_POOL_ID = "us-east-2_TaC1L1rt9";
  // const DEVELOP_COGNITO_IDENTITY_POOL_ID =
  //   "us-east-2:624fdbfa-b5c1-48cb-841a-25896c0880dd";
  // const PRODUCTION_COGNITO_IDENTITY_POOL_ID =
  //   "us-east-2:0a5dadf7-279a-4a80-80ef-55154eceb314";

  async function create(data: any) {
    try {
      // Clean and validate email - trim whitespace and convert to lowercase
      // Remove any non-printable characters that might cause Cognito validation to fail
      let cleanedEmail = (data.email || '').trim().toLowerCase().replace(/[\x00-\x1F\x7F]/g, '');
      const cleanedName = (data.name || '').trim().replace(/[\x00-\x1F\x7F]/g, '');
      
      if (!cleanedEmail || !cleanedName) {
        showError('Email and Name are required');
        return;
      }

      // Additional sanitization: ensure email doesn't have leading/trailing dots or multiple consecutive dots
      cleanedEmail = cleanedEmail.replace(/^\.+|\.+$/g, '').replace(/\.{2,}/g, '.');

      // Validate email format before sending to Cognito
      // Cognito accepts: Unicode letters, marks, symbols, numbers, and punctuation
      // Standard email pattern: local@domain.tld
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(cleanedEmail)) {
        showError('Please enter a valid email address');
        return;
      }

      // Ensure email length is reasonable (Cognito has limits)
      if (cleanedEmail.length > 320) {
        showError('Email address is too long');
        return;
      }

      const user = await signUp({
        username: cleanedEmail,
        password: data.password,
        options: {
          userAttributes: {
            name: cleanedName,
            email: cleanedEmail,
          },
        },
      });
      await addUserToGroup(cleanedEmail, data.group ? data.group : 'admin');
    } catch (err: Error | any) {
      // console.log(err);
      let errorMessage = 'Error occurred';
      if (typeof err.message === 'string') {
        errorMessage = err.message;
        // Provide more user-friendly error messages
        if (errorMessage.includes('username') && errorMessage.includes('constraint')) {
          errorMessage = 'Invalid email format. Please use a valid email address.';
        } else if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
          errorMessage = 'An account with this email already exists.';
        }
      } else if (err.errors && err.errors.length > 0) {
        errorMessage = err.errors.map((e: any) => e.message || e).join(', ');
      }
      showError(errorMessage);
    }
  }

  async function createMockAdmins() {
    try {
      for (let i = 31; i < 41; i++) {
        const name = `waiterFCP${i}`;
        const adminData = {
          name: name,
          password: '12345678',
          email: `${name}@anyware.software`,
          group: 'staff',
        };

        // Register Admin
        await create(adminData);
      }
    } catch (err: Error | any) {
      // console.log(err);
      showError(
        typeof err.message === 'string' ? err.message : 'Error occurred',
      );
    }
  }

  async function adminsConfig(apiPath: string, queryString: any, body?: any) {
    let apiName = AdminsConstants.ADMINS_API;
    let path = apiPath;
    let myInit = {
      body: body,
      queryParams: queryString,
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `${(await Auth.currentSession())
        //   .getAccessToken()
        //   .getJwtToken()}`,

        Authorization: `${(
          await fetchAuthSession()
        ).tokens?.accessToken.toString()}`,
      },
    };

    return {
      apiName,
      path,
      myInit,
    };
  }

  async function listGroupsForUser(username: string) {
    let groups: any[] = [];
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      const result: any = await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminListGroupsForUser(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });

      for (let group of result.Groups || []) {
        groups.push(group['GroupName']);
      }
      return groups;
    } catch (err: Error | any) {
      console.log('Error listing groups for user:', err);
      return groups; // Return empty array on error
    }
  }

  async function listAllGroups() {
    let groups: any[] = [];
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      const result: any = await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.listGroups(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Limit: 60,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });

      for (let group of result.Groups || []) {
        groups.push(group['GroupName']);
      }
      return groups;
    } catch (err: Error | any) {
      console.log('Error listing groups:', err);
      return groups; // Return empty array on error
    }
  }

  async function addUserToGroup(username: string, groupname: string) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminAddUserToGroup(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
            GroupName: groupname,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });
      showConfirm('User added to group successfully.');
    } catch (err: Error | any) {
      console.log('Error adding user to group:', err);
      // Don't show error for group operations - user creation succeeded
      // showError(err.message);
    }
  }

  async function removeUserFromGroup(username: string, groupname: string) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminRemoveUserFromGroup(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
            GroupName: groupname,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });
      showConfirm('User removed from group successfully.');
    } catch (err: Error | any) {
      console.log('Error removing user from group:', err);
      showError(err.message);
    }
  }

  async function fetch(
    nextToken: string,
    searchText: string,
    limit: number,
    type: any,
  ) {
    try {
      let listing: any[] = [];

      if (paginationList.length === 0 || type) {
        const credentials: any = (await fetchAuthSession()).credentials;
        AWS.config.credentials = credentials;
        AWS.config.region = 'us-east-2';
        new AWS.CognitoIdentityCredentials(
          {
            IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
          },
          { credentials: credentials },
        );
        const cognitoidentityserviceprovider =
          new AWS.CognitoIdentityServiceProvider();
        
        const result: any = await new Promise((resolve, reject) => {
          cognitoidentityserviceprovider.listUsers(
            {
              UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
              ...(nextToken ? { PaginationToken: nextToken } : {}),
            },
            function (err, data) {
              if (err) {
                reject(err);
              } else {
                resolve(data);
              }
            },
          );
        });

        nextToken = result.PaginationToken || '';
        let cognitoObj = result.Users || [];

        for (let user of cognitoObj) {
          let { Attributes, ...userData } = user;

          for (let attr of user['Attributes'] || []) {
            userData[attr['Name']] = attr['Value'];
          }
          
          // Handle top-level user properties
          if (userData['Username']) {
            userData['id'] = userData['Username'];
          }
          if (userData['UserCreateDate']) {
            userData['createdAt'] = userData['UserCreateDate'] instanceof Date 
              ? userData['UserCreateDate'].toISOString()
              : new Date(userData['UserCreateDate']).toISOString();
          }
          if (userData['email']) {
            userData['email'] = userData['email'];
          }
          
          listing.push(userData);
        }
        dispatch(setPaginationList(listing));
      } else {
        listing = paginationList;
      }

      if (searchText.length > 0) {
        let filteredData = listing.filter((item: any) => {
          return item.email?.toLowerCase().includes(searchText.toLowerCase());
        });
        dispatch(setListing(filteredData));
      } else {
        dispatch(setListing(listing));
      }
    } catch (err: Error | any) {
      console.log('Error listing users:', err);
      showError(
        typeof err.message === 'string' ? err.message : 'Error occurred',
      );
    }
  }

  async function byGroup(
    nextToken: string,
    limit: number,
    groupName: string,
    searchText: string | null = '',
    isEvent?: boolean,
  ) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();

      let currentNextToken: any;
      let currentListing: any[] = [];

      do {
        const result: any = await new Promise((resolve, reject) => {
          cognitoidentityserviceprovider.listUsersInGroup(
            {
              UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
              GroupName: groupName,
              ...(currentNextToken ? { NextToken: currentNextToken } : {}),
            },
            function (err, data) {
              if (err) {
                reject(err);
              } else {
                resolve(data);
              }
            },
          );
        });

        currentNextToken = result.NextToken;
        currentListing = [...currentListing, ...(result.Users || [])];
      } while (currentNextToken);

      let groupMembers: any[] = [];
      for (let user of currentListing) {
        let { Attributes, ...userData } = user;

        for (let attr of user['Attributes'] || []) {
          userData[attr['Name']] = attr['Value'];
        }
        
        // Handle top-level user properties
        if (userData['Username']) {
          userData['id'] = userData['Username'];
        }
        if (userData['UserCreateDate']) {
          userData['createdAt'] = userData['UserCreateDate'] instanceof Date 
            ? userData['UserCreateDate'].toISOString()
            : new Date(userData['UserCreateDate']).toISOString();
        }
        if (userData['email']) {
          userData['email'] = userData['email'];
        }
        
        if (userData['Username']) {
          groupMembers.push(userData);
        }
      }

      // Filter groupMembers based on searchText
      if (searchText && searchText !== '') {
        if (searchText && searchText.trim() !== '') {
          groupMembers = groupMembers.filter((member) =>
            member.name?.toLowerCase().includes(searchText.toLowerCase()),
          );
        }
      }

      if (!isEvent) {
        switch (groupName) {
          case 'admin':
            dispatch(setListing(groupMembers));
            break;
          case 'staff':
            dispatch(setWaiterListing(groupMembers));
            break;
        }
      }

      return groupMembers;
    } catch (err: Error | any) {
      console.log('Error listing users in group:', err);
      showError(
        typeof err.message === 'string' ? err.message : 'Error occurred',
      );
    }
  }

  async function get(username: string) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      const single: any = await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminGetUser(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });

      let { UserAttributes, ...userData } = single;

      for (let attr of single['UserAttributes'] || []) {
        userData[attr['Name']] = attr['Value'];
      }
      
      // Handle top-level user properties
      if (userData['Username']) {
        userData['id'] = userData['Username'];
      }
      if (userData['UserCreateDate']) {
        userData['createdAt'] = userData['UserCreateDate'] instanceof Date 
          ? userData['UserCreateDate'].toISOString()
          : new Date(userData['UserCreateDate']).toISOString();
      }
      if (userData['email']) {
        userData['email'] = userData['email'];
      }
      if (userData['Username']) {
        userData['Groups'] = await listGroupsForUser(userData['Username']);
      }

      return userData;
    } catch (err: Error | any) {
      console.log('Error getting user:', err);
      showError(
        typeof err.message === 'string' ? err.message : 'Error occurred',
      );
    }
  }

  async function enableUser(username: string) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminEnableUser(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });
      showConfirm('User enabled successfully.');
    } catch (err: Error | any) {
      console.log('Error enabling user:', err);
      showError(err.message);
    }
  }

  async function disableUser(username: string) {
    try {
      const credentials: any = (await fetchAuthSession()).credentials;
      AWS.config.credentials = credentials;
      AWS.config.region = 'us-east-2';
      new AWS.CognitoIdentityCredentials(
        {
          IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
        },
        { credentials: credentials },
      );
      const cognitoidentityserviceprovider =
        new AWS.CognitoIdentityServiceProvider();
      
      await new Promise((resolve, reject) => {
        cognitoidentityserviceprovider.adminDisableUser(
          {
            UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
            Username: username,
          },
          function (err, data) {
            if (err) {
              reject(err);
            } else {
              resolve(data);
            }
          },
        );
      });
      showConfirm('User disabled successfully.');
    } catch (err: Error | any) {
      console.log('Error disabling user:', err);
      showError(err.message);
    }
  }

  async function deleteUser(params: any) {
    // Handle both { id, listing } object and simple id string
    const userId = typeof params === 'string' ? params : params.id;
    const providedListing = typeof params === 'object' ? params.listing : undefined;
    const currentListing = providedListing || listing || [];
    
    const credentials: any = (await fetchAuthSession()).credentials;
    AWS.config.credentials = credentials;
    AWS.config.region = 'us-east-2';
    new AWS.CognitoIdentityCredentials(
      {
        IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
      },
      { credentials: credentials },
    );
    const cognitoidentityserviceprovider =
      new AWS.CognitoIdentityServiceProvider();
    cognitoidentityserviceprovider.adminDeleteUser(
      {
        UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
        Username: userId,
      },
      function (err, data) {
        if (data) {
          let newList = currentListing.filter((admin: any) => {
            return admin.id !== userId;
          });
          dispatch(setPaginationList(newList));
          dispatch(setListing(newList));
          showConfirm('User deleted successfully.');
        }
        if (err) {
          console.log("error deleting user", { err });
          showError(
            typeof err.message === 'string'
              ? err.message
              : 'Error occurred while deleting the user',
          );
        }
      },
    );
  }

  async function adminChangeUserPassword(username: string, password: string) {
    // const credentials = await Auth.currentCredentials();
    const credentials: any = (await fetchAuthSession()).credentials;
    AWS.config.credentials = credentials;
    AWS.config.region = 'us-east-2';
    new AWS.CognitoIdentityCredentials(
      {
        IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
      },
      { credentials: credentials },
    );
    const cognitoidentityserviceprovider =
      new AWS.CognitoIdentityServiceProvider();
    cognitoidentityserviceprovider.adminSetUserPassword(
      {
        UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
        Username: username,
        Password: password,
        Permanent: true,
      },
      function (err, data) {
        if (data) {
          showConfirm('User password has changed successfully.');
        }
        if (err) {
          showError('Unauthorized Action !');
          // console.log("error changing user password", { err });
          // showError(
          //   typeof err.message === "string"
          //     ? err.message
          //     : "Error occurred while changing user password"
          // );
        }
      },
    );
  }

  async function adminUpdateUserAttributes(
    username: string,
    email: string,
    name: string,
    picture: string,
  ) {
    const userAttrs: any[] = [];
    if (name) userAttrs.push({ Name: 'name', Value: name });
    if (email) {
      userAttrs.push({
        Name: 'email',
        Value: `${email}`,
      });
      userAttrs.push({
        Name: 'email_verified',
        Value: 'true',
      });
    }
    if (picture) userAttrs.push({ Name: 'picture', Value: picture });
    // const credentials = await Auth.currentCredentials();
    const credentials: any = (await fetchAuthSession()).credentials;
    AWS.config.credentials = credentials;
    AWS.config.region = 'us-east-2';
    new AWS.CognitoIdentityCredentials(
      {
        IdentityPoolId: DEVELOP_COGNITO_IDENTITY_POOL_ID,
      },
      { credentials: credentials },
    );
    const cognitoidentityserviceprovider =
      new AWS.CognitoIdentityServiceProvider();
    cognitoidentityserviceprovider.adminUpdateUserAttributes(
      {
        UserPoolId: DEVELOP_COGNITO_USER_POOL_ID,
        Username: username,
        UserAttributes: userAttrs,
      },
      function (err, data) {
        if (data) {
          showConfirm('User data has changed successfully.');
        }
        if (err) {
          showError(
            typeof err.message === 'string'
              ? err.message
              : 'Error occurred while changing user data',
          );
        }
      },
    );
  }

  //v5
  // async function changePassword(oldPassword: string, newPassword: string) {
  //   try {
  //     const user = await Auth.currentAuthenticatedUser();
  //     const result = await Auth.changePassword(user, oldPassword, newPassword);
  //     showConfirm(result);
  //   } catch (err: Error | any) {
  //     console.log(err);
  //     showError(
  //       typeof err.message === "string" ? err.message : "Error occurred"
  //     );
  //   }
  // }

  async function changePassword(oldPassword: string, newPassword: string) {
    try {
      // console.log(oldPassword, newPassword);

      await updatePassword({ oldPassword, newPassword });
      showConfirm('success');
    } catch (err: Error | any) {
      console.log(err);
      showError(
        typeof err.message === 'string' ? err.message : 'Error occurred',
      );
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'email',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Date',
    },
    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];

  const dataCells: readonly string[] = ['email', 'name'];

  function isAdmin(session: any) {
    if (session && session['cognito:groups']) {
      return (
        session['cognito:groups'].find((group: any) => group === 'admin') !==
        undefined
      );
    }
  }

  async function isCurrentAuthUser(resourceUsername: string): Promise<boolean> {
    const { username } = await getCurrentUser();
    return username === resourceUsername;
  }

  const options: any[] = [];

  for (let option of listing) {
    options.push({ label: option.name, value: option.sub });
  }
  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;

  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByGroup`] = byGroup;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}CreateMockAdmins`] = createMockAdmins;
  api[`${listingName}Update`] = () => {};
  api[`${listingName}Trash`] = () => {};
  api[`${listingName}BulkTrash`] = () => {};
  api[`${listingName}Delete`] = deleteUser;
  api[`${listingName}UpdateUserAttrs`] = adminUpdateUserAttributes;
  api[`${listingName}ChangeUserPassword`] = adminChangeUserPassword;
  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api['isAdmin'] = isAdmin;
  api['isCurrentAuthUser'] = isCurrentAuthUser;
  api['changePassword'] = changePassword;
  api[`${listingName}ListGroupsForUser`] = listGroupsForUser;
  api[`${listingName}ListAllGroups`] = listAllGroups;
  api[`addUserToGroup`] = addUserToGroup;
  api[`removeUserFromGroup`] = removeUserFromGroup;
  api[`${listingName}DisableUser`] = disableUser;
  api[`${listingName}EnableUser`] = enableUser;
  api[`${listingName}CognitoGroups`] = COGNITO_GROUPS;
  api[`${listingName}ChangeListingForGroup`] = (listing: any[]) => {
    dispatch(setDispatcherListing(listing));
    dispatch(
      setFilters(
        listing.map((model: any) =>
          model.Attributes[2] ? model.Attributes[2].Value : model.Username,
        ),
      ),
    );
  };
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) =>
    dispatch(setSelectedFilters(filters));
  api[`${listingName}ChangeFilters`] = (filters: string[]) =>
    dispatch(setFilters(filters));
  api[`${listingName}ChangePagination`] = (listing: any[]) =>
    dispatch(setPaginationList(listing));
  api[`${listingName}ChangeType`] = (AdminType: any) =>
    dispatch(setAdminType(AdminType));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = listing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));

  return api;
};

export default useResource;
